import axios from 'axios';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { apiBaseUrl } from '../../util/api';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  TRANSITION_ACCEPT_ASSIGNED_GIG,
  TRANSITION_AUTO_CONFIRM_ASSIGNED_GIG,
  TRANSITION_DECLINE_ASSIGNED_GIG_BUSINESS,
  TRANSITION_GIG_START_NOTIF_BEFORE_1,
  TRANSITION_GIG_START_NOTIF_BEFORE_24,
  TRANSITION_PROVIDER_CANCEL,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_1,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_24,
  TRANSITION_ZERO_CONFIRM_PAYMENT,
  TRANSITION_ZERO_DECLINE_PAYMENT,
  TRANSITION_ZERO_REQUEST_PAYMENT,
} from '../../util/transaction';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
// const RESULT_PAGE_SIZE = 42;
const RESULT_PAGE_SIZE = 10;

function prepareCurrencyString(amount) {
  if (!amount) return `0:00`;
  const dollar = Math.floor(amount / 100);
  const cents = amount % 100;

  const finalAmount = `${dollar}.${cents.toString().padStart(2, '0')}`;

  return finalAmount;
}

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const FETCH_OWN_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_OWN_LISTINGS_REQUEST';
export const FETCH_OWN_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_OWN_LISTINGS_SUCCESS';
export const FETCH_OWN_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_OWN_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_SUCCESS_FROM_CG = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS_FROM_CG';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';
export const RESET_CLOSE_LISTING = 'app/ManageListingsPage/RESET_CLOSE_LISTING';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const FETCH_INVOICE_UPDATE_TIME = 'app/ManageListingsPage/FETCH_INVOICE_UPDATE_TIME';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  closeListingInProgress: false,
  listingType: null,
  invoiceData: [],
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const ownListings = state.ownEntities?.ownListing || {};
  const oldListing = ownListings[listingEntity.id.uuid];
  const updatedListing =
    typeof oldListing === 'object' && oldListing !== null
      ? { ...oldListing, attributes: listingEntity.attributes }
      : listingEntity;
  const ownListingEntities = {
    ...ownListings,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
        listingType: '',
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
        listingType: 'listing',
      };
    case FETCH_OWN_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
        listingType: '',
      };
    case FETCH_OWN_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
        listingType: 'ownListing',
      };
    case FETCH_OWN_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
        // currentPageResultIds:[],
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_SUCCESS_FROM_CG:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
        currentPageResultIds: payload.myIds,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
        closeListingInProgress: true,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
        closeListingInProgress: false,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
        closeListingInProgress: false,
      };
    }
    case RESET_CLOSE_LISTING:
      return { ...state, closingListingError: null, closeListingInProgress: false };
    case FETCH_INVOICE_UPDATE_TIME: {
      return {
        ...state,
        invoiceData: [payload],
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingSuccessFromCG = response => ({
  type: OPEN_LISTING_SUCCESS_FROM_CG,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const resetCloseListingStatus = () => ({
  type: RESET_CLOSE_LISTING,
});

export const queryOwnListingsRequest = queryParams => ({
  type: FETCH_OWN_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryOwnListingsSuccess = response => ({
  type: FETCH_OWN_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryOwnListingsError = e => ({
  type: FETCH_OWN_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const InvoiceUpdatedTime = res => ({
  type: FETCH_INVOICE_UPDATE_TIME,
  payload: res,
});

const isListingCreationEnabled = async () => {
  const url = `${apiBaseUrl()}/api/gig-creation/status/user`;
  await axios.get(url, { withCredentials: true });
};

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryOwnListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, per_page: perPage, pub_isChatWithAdminListing: false };
  let apiResponse;
  return sdk.ownListings
    .query(params)
    .then(response => {
      apiResponse = response;
      const listingIds = response.data.data.map(listing => listing.id.uuid);
      return axios.get(`${apiBaseUrl()}/api/timeStatement/listings`, {
        params: { ids: listingIds, excludeBothConfirmedSide: false },
      });
    })
    .then(resp => {
      const timestatments = resp.data;
      const timestatementObj = timestatments.reduce((acc, TimeStatement) => {
        if (acc[TimeStatement.listingId] === undefined) {
          acc[TimeStatement.listingId] = [];
        }
        acc[TimeStatement.listingId].push(TimeStatement);
        return acc;
      }, {});
      apiResponse.data.data.forEach(listing => {
        listing.attributes.publicData.workers = timestatementObj[listing.id.uuid];
      });
      dispatch(addOwnEntities(apiResponse));
      dispatch(queryOwnListingsSuccess(apiResponse));
      return apiResponse;
    })
    .catch(e => {
      dispatch(queryOwnListingsError(storableError(e)));
      throw e;
    });
};
export const dbInvoiceData = id => (dispatch, getState, sdk) => {
  // console.log(333, id);

  return axios
    .get(`${apiBaseUrl()}/api/stripe/getDBDataByListingId`, {
      params: { listingId: id },
      withCredentials: true,
    })
    .then(res => {
      // console.log(556, res);
      dispatch(InvoiceUpdatedTime(res?.data));
    })
    .catch(err => {
      console.error('Axios request failed. Error:', err);
    });
};
export const closedTabListings = queryParams => async (dispatch, getState, sdk) => {
  const { perPage, ...rest } = queryParams;
  await dispatch(fetchCurrentUser());
  const currentUser = getState().user.currentUser;
  const authorId = currentUser?.id?.uuid;
  let apiRes;
  // console.log(333, currentUser, authorId);
  const params = {
    ...rest,
    authorId,
    states: 'closed',
    per_page: perPage,
    pub_isChatWithAdminListing: false,
  };
  dispatch(queryListingsRequest(queryParams));

  return axios
    .post(`${apiBaseUrl()}/api/gigSearch`, params)
    .then(res => {
      apiRes = res;
      const listingIds = res.data.data.data.map(listing => listing.id.uuid);
      return axios.get(`${apiBaseUrl()}/api/timeStatement/listings`, {
        params: { ids: listingIds, excludeBothConfirmedSide: false },
      });
    })
    .then(resp => {
      const timestatments = resp.data;
      const timestatementObj = timestatments.reduce((acc, TimeStatement) => {
        if (acc[TimeStatement.listingId] === undefined) {
          acc[TimeStatement.listingId] = [];
        }
        acc[TimeStatement.listingId].push(TimeStatement);
        return acc;
      }, {});
      apiRes.data.data.data.forEach(listing => {
        listing.attributes.publicData.workers = timestatementObj[listing.id.uuid];
      });
      // console.log(444, res.data);
      const ClosedResp = apiRes?.data;
      dispatch(addMarketplaceEntities(ClosedResp));
      dispatch(queryListingsSuccess(ClosedResp));
      // dispatch(closeListingSuccess(ClosedResp));
      return ClosedResp;
    })
    .catch(e => {
      console.log(e);
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};
export const draftTabListings = queryParams => async (dispatch, getState, sdk) => {
  const { perPage, ...rest } = queryParams;
  await dispatch(fetchCurrentUser());
  const currentUser = getState().user.currentUser;
  const authorId = currentUser?.id?.uuid;
  // console.log(333, currentUser, authorId);
  const params = {
    ...rest,
    authorId,
    states: 'draft',
    per_page: perPage,
    pub_isChatWithAdminListing: false,
  };
  dispatch(queryListingsRequest(queryParams));

  return axios
    .post(`${apiBaseUrl()}/api/gigDraftSearch`, params)
    .then(res => {
      const DraftResp = res?.data;
      // console.log(777, res.data);
      dispatch(addMarketplaceEntities(DraftResp));
      dispatch(queryListingsSuccess(DraftResp));
      return DraftResp;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};
export const allTabListings = queryParams => async (dispatch, getState, sdk) => {
  const { perPage, ...rest } = queryParams;
  await dispatch(fetchCurrentUser());
  const currentUser = getState().user.currentUser;
  const authorId = currentUser?.id?.uuid;
  // console.log(333, currentUser, authorId);
  const params = {
    ...rest,
    authorId,
    states: 'draft',
    per_page: perPage,
    pub_isChatWithAdminListing: false,
  };
  dispatch(queryListingsRequest(queryParams));

  return axios
    .post(`${apiBaseUrl()}/api/allDraftSearch`, params)
    .then(res => {
      const Resp = res?.data;
      // console.log(777, res.data);
      dispatch(addOwnEntities(Resp));
      dispatch(queryListingsSuccess(Resp));
      return Resp;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));
  let returnResp;
  const url = `${apiBaseUrl()}/api/listing-refunds/${listingId}`;

  return (
    axios
      .post(url, { closeDate: new Date() })
      .then(() => sdk.ownListings.show({ id: listingId }, { expand: true }))
      .then(response => {
        returnResp = response;
        return response;
      })
      .then(response => {
        //query currentListingTxs
        const txQueryParams = {
          listingId,
          lastTransitions: [
            TRANSITION_ACCEPT_ASSIGNED_GIG,
            TRANSITION_ZERO_CONFIRM_PAYMENT,
            TRANSITION_ZERO_REQUEST_PAYMENT,
            TRANSITION_AUTO_CONFIRM_ASSIGNED_GIG,
            TRANSITION_GIG_START_NOTIF_BEFORE_1,
            TRANSITION_GIG_START_NOTIF_BEFORE_24,
          ],
          include: ['provider', 'provider.profile', 'customer', 'customer.profile', 'booking'],
        };
        return sdk.transactions.query(txQueryParams);
      })
      .then(async response => {
        //transtion all possible txs
        const txs = response.data.data || [];
        // console.log(74152, txs);
        const transitionPromises = txs.map(tx =>
          sdk.transactions.transition(
            {
              id: tx.id,
              transition:
                tx.attributes.lastTransition === TRANSITION_ZERO_REQUEST_PAYMENT
                  ? TRANSITION_ZERO_DECLINE_PAYMENT
                  : // : tx.attributes.lastTransition === TRANSITION_ZERO_CONFIRM_PAYMENT
                  // ? TRANSITION_PROVIDER_CANCEL
                  tx.attributes.lastTransition === TRANSITION_GIG_START_NOTIF_BEFORE_24
                    ? TRANSITION_PROVIDER_CANCEL_BEFORE_START_24
                    : tx.attributes.lastTransition === TRANSITION_GIG_START_NOTIF_BEFORE_1
                      ? TRANSITION_PROVIDER_CANCEL_BEFORE_START_1
                      : TRANSITION_PROVIDER_CANCEL,
              params: {},
            },
            {
              expand: true,
              include: ['provider', 'provider.profile', 'customer', 'customer.profile', 'booking'],
            }
          )
        );
        await Promise.all(transitionPromises);
        return response;
      })
      .then(response => {
        //Update User or listing datas if needed
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .then(response => {
        const txs = getMarketplaceEntities(getState(), [
          ...(response.data.data?.map(t => ({ id: t.id, type: t.type })) || []),
        ]);
        // console.log(4578, txs);
        //Send Mail to workers
        // const workersMails = txs.map(tx => {
        //   const url = `${apiBaseUrl()}/api/sendrid`;
        //   const data = { ...tx };
        //   return axios.post(url, data);
        // });
        // console.log(457812, workersMails);
        // Promise.allSettled(workersMails);
        return txs;
      })
      // .then(response => {
      //   return response;
      // })
      .then(response => {
        //Process Refund
        // const url = `${apiBaseUrl()}/api/users/processRefund`
        dispatch(closeListingSuccess(returnResp));
        return returnResp;
      })
      .catch(e => {
        dispatch(closeListingError(storableError(e)));
        throw e;
      })
  );
};

export const openListing = (listingId, params, search) => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));
  const currentPageResultIds = getState().ManageListingsPage.currentPageResultIds;
  // console.log(555, listingId, currentPageResultIds);
  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      const myIds = currentPageResultIds?.filter(id => id.uuid !== listingId.uuid);
      const dispatchFunct =
        search?.tab === 'ClosedGigs' ? openListingSuccessFromCG : openListingSuccess;
      const aptResp =
        search?.tab === 'ClosedGigs'
          ? { ...response, data: { ...response.data, myIds } }
          : response;
      // console.log(111, aptResp);

      dispatch(dispatchFunct(aptResp));
      return response;
    })
    .then(res => loadData())
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};
const createDraftLiting = data => sdk => {
  return isListingCreationEnabled()
    .then(() => sdk.ownListings.createDraft(data))
    .then(response => {
      return response.data;
    });
};
export const copyOwnListing = listingId => (dispatch, getState, sdk) => {
  let gigId = null;
  const url = `${apiBaseUrl()}/api/user/customid/listing`;
  return isListingCreationEnabled()
    .then(() => axios.post(url))
    .then(resp => {
      const { customId } = resp.data;
      gigId = customId;
      return sdk.ownListings.show({ id: listingId });
    })
    .then(response => {
      const { data } = response?.data;

      const { attributes } = data;
      const {
        createdAt,
        deleted,
        state,
        metadata,
        geolocation,
        price,
        publicData: { appliedCust, hasApplied, amountString, ...restPublicData },
        privateData: { assigendWorkers, acceptedWorkers, workerCanApply, ...restPrivateData },
        ...finalAttributes
      } = attributes;

      const geolocationMaybe = geolocation ? { geolocation } : {};
      const priceMayBe = price ? { price } : {};

      const finalData = {
        ...priceMayBe,
        ...geolocationMaybe,
        ...finalAttributes,
        publicData: {
          ...restPublicData,
          gigEndDate: null,
          gigEndDateInNumberFormat: null,
          gigPublishedDate: null,
          gigStartDate: null,
          gigStartDateInNumberFormat: null,
          searchtime: null,
          oneTimeScheduleList: null,
          scheduleType: null,
          isCloneType: true,
          gigId,
          amountString: priceMayBe.price?.amount
            ? prepareCurrencyString(priceMayBe.price.amount)
            : 0,
        },
        privateData: {
          ...restPrivateData,
        },
      };
      return createDraftLiting(finalData, {
        expand: true,
      })(sdk);
    });
};

export const loadData = (params, search) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const { tab } = queryParams || 'AllGigs';
  // console.log(233, tab);
  if (tab === 'ClosedGigs') {
    // console.log(66)
    return closedTabListings({
      ...queryParams,
      page,
      // tab,
      perPage: RESULT_PAGE_SIZE,
      include: ['images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
    });
  } else if (tab === 'DraftGigs') {
    return draftTabListings({
      ...queryParams,
      page,
      //  tab,
      perPage: RESULT_PAGE_SIZE,
      include: ['images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
    });
  }
  // else if (tab === 'AllGigs') {
  //   return allTabListings({
  //     ...queryParams,
  //     page,
  //     //  tab,
  //     perPage: RESULT_PAGE_SIZE,
  //     include: ['images'],
  //     'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  //     'limit.images': 1,
  //   });
  // }
  else {
    return queryOwnListings({
      ...queryParams,
      page,
      //  tab,
      perPage: RESULT_PAGE_SIZE,
      include: ['images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
    });
  }
};
